/**
 * Which domain a cookie should be scoped to, decided by asking the browser.
 *
 * The script wrote `document.cookie` with no `Domain` until 13 August 2026, so
 * every cookie it set was host-only. That made `klant.nl`, `www.klant.nl` and
 * `blog.klant.nl` three different visitors on every customer without
 * first-party (which is most of them), and it broke the journey on every
 * navigation between them. The server side already got this right through
 * `cookieDomainFor`.
 *
 * PROBED, NOT PARSED. Stripping labels until it "looks like a domain" needs a
 * public suffix list to know that `klant.co.uk` is registrable while `co.uk` is
 * not, and that list is fifteen thousand lines that go stale. The browser
 * already enforces the rule: a cookie set on a public suffix is dropped without
 * a word. So try the shortest candidate first and keep widening until one
 * sticks, which is what Google Analytics has done for a decade.
 *
 * The probe is injected so this decision is testable without a browser, which
 * is the only place the alternative could be checked.
 */
export function pickCookieDomain(
  hostname: string,
  accepts: (candidate: string) => boolean,
): string | null {
  // A single-label host (localhost) and an IP address have no parent domain to
  // widen to, and a `Domain` attribute on either is rejected outright.
  if (!hostname || hostname.indexOf(".") < 0) return null;
  if (/^[\d.]+$/.test(hostname)) return null;

  const labels = hostname.split(".");
  for (let take = 2; take <= labels.length; take++) {
    const candidate = labels.slice(labels.length - take).join(".");
    if (accepts(candidate)) return candidate;
  }
  return null;
}
